import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  Crown,
  Package,
  Globe,
  Eye,
  UserCog,
  X,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { admin } from '../api';

const planColors = {
  free: 'bg-gray-500/20 text-gray-400',
  starter: 'bg-blue-500/20 text-blue-400',
  advanced: 'bg-purple-500/20 text-purple-400',
  business: 'bg-amber-500/20 text-amber-400',
  // Legacy mappings
  pro: 'bg-blue-500/20 text-blue-400',
  unlimited: 'bg-purple-500/20 text-purple-400',
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planToSet, setPlanToSet] = useState(null);
  const [updating, setUpdating] = useState(false);

  const fetchUsers = async (page = 1, searchQuery = search) => {
    setLoading(true);
    try {
      const data = await admin.getUsers(page, 20, searchQuery);
      setUsers(data.users);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await admin.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    fetchUsers(1, searchInput);
  };

  const handlePageChange = (newPage) => {
    fetchUsers(newPage);
  };

  const handlePlanChange = async () => {
    if (!selectedUser || !planToSet) return;
    
    setUpdating(true);
    try {
      await admin.updateUserPlan(selectedUser.id, planToSet);
      // Update local state
      setUsers(users.map(u => 
        u.id === selectedUser.id ? { ...u, plan: planToSet } : u
      ));
      setShowPlanModal(false);
      setSelectedUser(null);
      setPlanToSet(null);
      // Refresh stats
      fetchStats();
    } catch (err) {
      console.error('Failed to update plan:', err);
      alert('Failed to update plan');
    } finally {
      setUpdating(false);
    }
  };

  const openPlanModal = (user) => {
    setSelectedUser(user);
    setPlanToSet(user.plan || 'free');
    setShowPlanModal(true);
  };

  const StatCard = ({ icon: Icon, label, value, color = 'blue' }) => (
    <div className="bg-[#111118] rounded-xl p-4 border border-white/5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg bg-${color}-500/10 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-400`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-white">{value}</p>
          <p className="text-sm text-white/40">{label}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Crown className="w-5 h-5 text-yellow-500" />
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          </div>
          <p className="text-white/60">Manage users and their plans</p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Users" value={stats.users.total} color="blue" />
          <StatCard icon={Package} label="Total Products" value={stats.products.total} color="green" />
          <StatCard icon={Eye} label="Today's Loads" value={stats.today.widgetLoads} color="purple" />
          <StatCard icon={Users} label="Today's Visitors" value={stats.today.uniqueVisitors} color="orange" />
        </div>
      )}

      {/* Plan Distribution */}
      {stats && (
        <div className="bg-[#111118] rounded-xl p-4 border border-white/5">
          <h3 className="text-sm font-medium text-white/60 mb-3">Users by Plan</h3>
          <div className="flex gap-4">
            {Object.entries(stats.users.byPlan || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${planColors[plan] || planColors.free}`}>
                  {plan}
                </span>
                <span className="text-white font-semibold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email..."
            className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#111118] border border-white/10 text-white placeholder:text-white/40 focus:border-blue-500 focus:outline-none transition-colors"
          />
        </div>
        <button
          type="submit"
          className="px-6 py-3 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600 transition-colors"
        >
          Search
        </button>
      </form>

      {/* Users Table */}
      <div className="bg-[#111118] rounded-2xl border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-6 py-4 text-sm font-medium text-white/60">User</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-white/60">Plan</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-white/60">Products</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-white/60">Domain</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-white/60">Joined</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-white/60">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-white/40">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-white/40">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {user.image ? (
                          <img src={user.image} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-400">
                              {user.name?.charAt(0) || user.email?.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-white font-medium">{user.name}</p>
                          <p className="text-sm text-white/40">{user.email}</p>
                        </div>
                        {!user.emailVerified && (
                          <span className="px-2 py-0.5 rounded text-xs bg-yellow-500/20 text-yellow-400">
                            Unverified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-lg text-sm font-medium ${planColors[user.plan] || planColors.free}`}>
                        {user.plan || 'free'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="text-white">{user.productCount}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={user.domainCount > 0 ? 'text-emerald-400' : 'text-white/50'}>
                        {user.domainCount > 0 ? 'Activated / Set' : 'Deactivated / Not set'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white/60">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => openPlanModal(user)}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-white/60 hover:text-white"
                      >
                        <UserCog className="w-4 h-4" />
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/5">
            <p className="text-sm text-white/40">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white/60" />
              </button>
              <span className="px-4 py-2 text-white">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white/60" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Plan Change Modal */}
      <AnimatePresence>
        {showPlanModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPlanModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[#111118] rounded-2xl border border-white/10 overflow-hidden"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/5">
                <div>
                  <h3 className="text-lg font-semibold text-white">Manage User Plan</h3>
                  <p className="text-sm text-white/40">{selectedUser.email}</p>
                </div>
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <p className="text-white/60 text-sm mb-4">Select a plan for this user:</p>
                
                {['free', 'starter', 'advanced', 'business'].map((plan) => (
                  <button
                    key={plan}
                    onClick={() => setPlanToSet(plan)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                      planToSet === plan
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/10 hover:border-white/20 bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-lg text-sm font-medium ${planColors[plan]}`}>
                        {plan}
                      </span>
                      <span className="text-white/60">
                        {plan === 'free' && 'Basic features (5 products)'}
                        {plan === 'starter' && '$149/mo (150 products)'}
                        {plan === 'advanced' && '$299/mo (500 products)'}
                        {plan === 'business' && 'Custom (unlimited)'}
                      </span>
                    </div>
                    {planToSet === plan && (
                      <Check className="w-5 h-5 text-blue-400" />
                    )}
                  </button>
                ))}
                
                {selectedUser.plan !== planToSet && (
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-400">
                      This will change the user's plan from <strong>{selectedUser.plan || 'free'}</strong> to <strong>{planToSet}</strong>.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-end gap-3 p-6 border-t border-white/5">
                <button
                  onClick={() => setShowPlanModal(false)}
                  className="px-4 py-2 rounded-lg text-white/60 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePlanChange}
                  disabled={updating || selectedUser.plan === planToSet}
                  className="px-6 py-2 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {updating ? 'Updating...' : 'Update Plan'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsersPage;
